import React from "react";
import { render, screen, waitFor } from "@testing-library/react";
import "@testing-library/jest-dom";
import { MemoryRouter } from "react-router-dom";
import { CssVarsProvider } from "@mui/joy/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { EmbeddedCanvasRenderer } from "./EmbeddedCanvasRenderer";
import { CanvasRuntimeProvider } from "../CanvasRuntimeContext";

const mockQuery = jest.fn();
const mockFetch = jest.fn();
jest.mock("../../../../../utils/useAuthFetch", () => ({
  useAuthFetch: () => mockFetch,
}));
jest.mock("../../../hooks/useRelatedRecords", () => ({
  useRelatedRecords: (...args: unknown[]) => mockQuery(...args),
}));
jest.mock("../../../hooks/useViewDataQuery", () => ({
  useViewQuery: () => ({
    data: { id: "jobs", focus_table_id: "people-table", fields: [], joins: [] },
  }),
  useCreateRowMutation: () => ({ mutateAsync: jest.fn() }),
  useUpdateRowMutation: () => ({ mutateAsync: jest.fn() }),
}));
jest.mock("../../viewGrid", () => ({
  ViewGridNew: () => <div>Related rows</div>,
}));
jest.mock("./CanvasKanbanRenderer", () => ({
  CanvasKanbanRenderer: () => <div>Kanban collection</div>,
}));
jest.mock("./CanvasCalendarRenderer", () => ({
  CanvasCalendarRenderer: () => <div>Calendar collection</div>,
}));
jest.mock("../../viewGrid/useViewGridColumns", () => ({
  FileCell: ({ rowId, value }: { rowId: string; value: unknown }) => (
    <div>
      File for {rowId}: {String(value)}
    </div>
  ),
}));
const mockChild: any = {
  id: "child",
  name: "Related child",
  data_sources: [
    { alias: "secondary", view_id: "other", is_primary: false },
    { alias: "people", view_id: "employees", is_primary: true },
    { alias: "jobs", view_id: "jobs", is_primary: false },
  ],
  elements: ["people", "secondary"].map((alias, index) => ({
    id: `${alias}-related`,
    element_type: "WIDGET",
    grid_column: 1,
    grid_row: index * 4 + 1,
    column_span: 12,
    row_span: 4,
    config_json: {
      widget_type: "RELATED_RECORDS",
      data_source_alias: alias,
      related_records_config: {
        related_data_source_alias: "jobs",
        relationship_path: "jobs",
        display_fields: ["title"],
      },
    },
  })),
};
jest.mock("../../../hooks/useChildCanvasPreview", () => ({
  useChildCanvasPreview: () => ({
    canvas: mockChild,
    loading: false,
    error: null,
  }),
}));

test("embedded runtime dispatches related widgets and preserves a second-position primary source", async () => {
  mockQuery.mockReturnValue({
    data: null,
    page: 1,
    setPage: jest.fn(),
    isLoading: false,
  });
  mockFetch.mockImplementation(async (_url, description) =>
    description === "fetch canvas data"
      ? { can_create: false, can_edit: false, can_delete: false }
      : {
          rows: [
            { _rowId: "child-person", name: "Child", cv: "fictional.txt" },
          ],
        },
  );
  renderChild();
  await waitFor(() =>
    expect(mockQuery).toHaveBeenCalledWith(
      "child",
      "people-related",
      "child-person",
    ),
  );
  expect(mockQuery).toHaveBeenCalledWith("child", "secondary-related", null);
});

function renderChild() {
  const queryClient = new QueryClient();
  render(
    <QueryClientProvider client={queryClient}>
      <CssVarsProvider>
        <MemoryRouter>
          <CanvasRuntimeProvider
            value={{
              workspaceId: "ws",
              canvasId: "host",
              rowId: "host-person",
              rowData: { id: "host-person" },
              interactionBus: {
                getSelectedRow: () => null,
                getRuntimeFilters: () => [],
                upsertRuntimeFilter: () => {},
                removeRuntimeFilter: () => {},
                dispatchInteraction: () => {},
                selectedRows: {},
                runtimeFilters: {},
              },
            }}
          >
            <EmbeddedCanvasRenderer
              element={
                {
                  id: "embed",
                  type: "embedded_canvas",
                  props: {
                    childCanvasId: "child",
                    parentKeyField: "id",
                    childDataSourceAlias: "people",
                    childFilterField: "parent_id",
                  },
                  layout: {
                    gridColumn: 1,
                    gridRow: 1,
                    columnSpan: 12,
                    rowSpan: 8,
                  },
                } as any
              }
              parentData={{ id: "host-person" }}
              workspaceId="ws"
              parentCanvasId="host"
              allowCreate={false}
              allowEdit={false}
              allowDelete={false}
            />
          </CanvasRuntimeProvider>
        </MemoryRouter>
      </CssVarsProvider>
    </QueryClientProvider>,
  );
}

const originalElements = mockChild.elements;
test.each(["KANBAN", "CALENDAR", "HIERARCHY"])(
  "mixed %s child preserves the selected child record for FILE and RELATED_RECORDS",
  async (widget) => {
    mockQuery.mockClear();
    mockQuery.mockReturnValue({
      data: null,
      page: 1,
      setPage: jest.fn(),
      isLoading: false,
    });
    mockFetch.mockImplementation(async (_url, description) =>
      description === "fetch canvas data"
        ? { can_create: false, can_edit: false, can_delete: false }
        : {
            rows: [
              { _rowId: "child-person", name: "Child", cv: "fictional.txt" },
            ],
          },
    );
    mockChild.elements = [
      ...originalElements,
      {
        id: "collection",
        element_type: "WIDGET",
        grid_column: 1,
        grid_row: 10,
        column_span: 12,
        row_span: 4,
        config_json: {
          widget_type: widget,
          data_source_alias: "people",
          ...(widget === "HIERARCHY"
            ? {
                hierarchy_config: {
                  nodes: [
                    {
                      data_source_alias: "people",
                      table_id: "table",
                      id_field: "id",
                      label_field: "name",
                    },
                  ],
                  relationships: [
                    {
                      relationship_ids: ["manager"],
                      from_data_source_alias: "people",
                      to_data_source_alias: "people",
                      from_field: "manager",
                      to_field: "id",
                      reverse: true,
                    },
                  ],
                  layout: "list",
                },
              }
            : {}),
        },
      },
      {
        id: "file",
        element_type: "FIELD",
        grid_column: 1,
        grid_row: 15,
        column_span: 12,
        row_span: 4,
        config_json: { fieldType: "file", label: "CV" },
        data_binding: { data_source_alias: "people", field_path: "cv" },
      },
    ] as typeof mockChild.elements;
    renderChild();
    await waitFor(() =>
      expect(mockQuery).toHaveBeenCalledWith(
        "child",
        "people-related",
        "child-person",
      ),
    );
    expect(
      screen.getByText("File for child-person: fictional.txt"),
    ).toBeVisible();
    expect(screen.queryByText("Save row first")).not.toBeInTheDocument();
    expect(
      await screen.findByText(
        widget === "HIERARCHY"
          ? "Director"
          : widget === "KANBAN"
            ? "Kanban collection"
            : "Calendar collection",
      ),
    ).toBeVisible();
    mockChild.elements = originalElements;
  },
);

test("mixed embedded child keeps its record context and hides a nested saved FIELD", async () => {
  mockQuery.mockClear();
  mockQuery.mockReturnValue({
    data: null,
    page: 1,
    setPage: jest.fn(),
    isLoading: false,
  });
  mockFetch.mockImplementation(async (_url, description) =>
    description === "fetch canvas data"
      ? { can_create: false, can_edit: false, can_delete: false }
      : {
          rows: [
            {
              _rowId: "child-person",
              name: "Child",
              cv: "fictional.txt",
              secret: "Confidential value",
            },
          ],
        },
  );
  mockChild.elements = [
    ...originalElements,
    {
      id: "collection",
      element_type: "WIDGET",
      grid_column: 1,
      grid_row: 10,
      column_span: 12,
      row_span: 4,
      config_json: { widget_type: "KANBAN", data_source_alias: "people" },
    },
    {
      id: "details",
      element_type: "CONTAINER",
      grid_column: 1,
      grid_row: 15,
      column_span: 12,
      row_span: 4,
      config_json: { containerType: "SECTION" },
    },
    {
      id: "hidden-secret",
      element_type: "FIELD",
      parent_element_id: "details",
      grid_column: 1,
      grid_row: 1,
      column_span: 12,
      row_span: 1,
      config_json: { fieldType: "text", label: "Hidden nested field" },
      data_binding: { data_source_alias: "people", field_path: "secret" },
      is_visible: false,
    },
    {
      id: "file",
      element_type: "FIELD",
      grid_column: 1,
      grid_row: 20,
      column_span: 12,
      row_span: 4,
      config_json: { fieldType: "file", label: "CV" },
      data_binding: { data_source_alias: "people", field_path: "cv" },
    },
  ] as typeof mockChild.elements;

  renderChild();

  await waitFor(() =>
    expect(mockQuery).toHaveBeenCalledWith(
      "child",
      "people-related",
      "child-person",
    ),
  );
  expect(
    screen.getByText("File for child-person: fictional.txt"),
  ).toBeVisible();
  expect(screen.getByText("Kanban collection")).toBeVisible();
  expect(screen.queryByText("Hidden nested field")).not.toBeInTheDocument();
  expect(screen.queryByText("Confidential value")).not.toBeInTheDocument();
});

afterEach(() => {
  mockChild.elements = originalElements;
});

test("collection-only children retain their existing collection rendering", async () => {
  mockQuery.mockClear();
  mockChild.elements = [
    {
      id: "collection",
      element_type: "WIDGET",
      grid_column: 1,
      grid_row: 1,
      column_span: 12,
      row_span: 4,
      config_json: { widget_type: "KANBAN", data_source_alias: "people" },
    },
  ];
  renderChild();
  expect(await screen.findByText("Kanban collection")).toBeVisible();
  expect(mockQuery).not.toHaveBeenCalled();
});

const mockGraphRows = [
  { id: 0, name: "Director" },
  { id: 1, name: "Manager", manager: 0 },
];
jest.mock("../../../hooks/useAccumulatedViewRows", () => ({
  useAccumulatedViewRows: () => ({
    rows: mockGraphRows,
    totalCount: 2,
    hasMore: false,
    isLoading: false,
    isLoadingMore: false,
    error: null,
    loadMore: jest.fn(),
  }),
}));
test("actual embedded runtime reaches a bound hierarchy through shared dispatch", async () => {
  mockChild.elements = [
    {
      id: "hierarchy",
      element_type: "WIDGET",
      grid_column: 1,
      grid_row: 1,
      column_span: 12,
      row_span: 8,
      config_json: {
        widget_type: "HIERARCHY",
        hierarchy_config: {
          nodes: [
            {
              data_source_alias: "people",
              table_id: "table",
              id_field: "id",
              label_field: "name",
            },
          ],
          relationships: [
            {
              relationship_ids: ["manager"],
              from_data_source_alias: "people",
              to_data_source_alias: "people",
              from_field: "manager",
              to_field: "id",
              reverse: true,
            },
          ],
          layout: "radial",
        },
      },
    },
  ];
  renderChild();
  expect(await screen.findByText("Director")).toBeVisible();
  expect(screen.getByText("Manager")).toBeVisible();
});
